import { BaseService } from '@/infrastructure/api/base.service'
import type {
  GetBulkDownloadInfoResponse,
  RefreshPercentageResponse,
  BulkDownloadFileExistsResponse,
  BoReportDownloadLocation,
  RerunBulkDownloadResponse,
  GetFiltersUsedResponse,
  LeadOutcomesResponse,
  ExportedDataResponse,
  ExclusionsResponse,
  CrmErrorsResponse,
  GetCompaniesFromListResponse,
  GetContactsFromListResponse,
  LeadExclusionDetail,
} from '@/features/purchasehistory/types/bulk-download-detail.types'

export class BulkDownloadDetailService extends BaseService {
  constructor() {
    super('')
  }

  async getBulkDownloadInformation(bulkDownloadId: string): Promise<GetBulkDownloadInfoResponse> {
    return this.post('/V2/Export/GetBulkDownloadInformation', bulkDownloadId)
  }

  async refreshBulkDownloadPercentage(bulkDownloadId: string): Promise<RefreshPercentageResponse> {
    return this.post('/V2/Export/RefreshBulkDownloadPercentage', bulkDownloadId)
  }

  /** Old exports can outlive their file in storage; asked before every download. */
  async checkBulkDownloadFileExists(bulkDownloadId: string): Promise<BulkDownloadFileExistsResponse> {
    return this.get('/V2/Export/CheckBulkDownloadFileExists', { params: { bulkDownloadId } })
  }

  /**
   * DEV-7908: a BackOffice-index report over the server's size limit cannot be downloaded
   * through the web app; this says so and gives its Azure Storage path.
   */
  async getBoReportDownloadLocation(bulkDownloadId: string): Promise<BoReportDownloadLocation> {
    return this.get('/V2/Export/GetBoReportDownloadLocation', { params: { bulkDownloadId } })
  }

  /** Runs the export again — offered when its file no longer exists. */
  async rerunBulkDownload(bulkDownloadId: string): Promise<RerunBulkDownloadResponse> {
    return this.post('/V2/Export/RerunBulkDownload', { bulkDownloadId })
  }

  async downloadExcel(bulkDownloadId: string, fileName: string): Promise<void> {
    const response = await fetch(
      `/V2/Export/DownloadExcelFromBulkDownload?bulkDownloadId=${bulkDownloadId}`,
    )
    if (!response.ok) throw new Error('Failed to get file from the server.')
    // DEV-7799: a bulk download too large for one worksheet is stored as a zip of xlsx files,
    // so the extension has to come from the server rather than be assumed to be .xlsx.
    const extension = this.getFileExtensionFromResponse(response)
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${fileName}${extension}`
    link.click()
    setTimeout(() => window.URL.revokeObjectURL(url), 100)
  }

  getFileExtensionFromResponse(response: Response, defaultExtension = '.xlsx'): string {
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition)
    const serverFileName = match ? decodeURIComponent(match[1].trim().replace(/"$/, '')) : ''
    const dotIndex = serverFileName.lastIndexOf('.')

    return dotIndex > 0 ? serverFileName.substring(dotIndex) : defaultExtension
  }

  /**
   * Per-lead export outcomes, server-paged. `recorded: false` means the export predates
   * outcome tracking — not that it was clean.
   */
  async getBulkDownloadLeadOutcomes(
    bulkDownloadId: string,
    page: number,
    pageSize: number,
    status = '',
    shortOnly = false,
    search = '',
  ): Promise<LeadOutcomesResponse> {
    return this.post('/V2/Export/GetBulkDownloadLeadOutcomes', {
      bulkDownloadId,
      page,
      pageSize,
      status,
      shortOnly,
      search,
    })
  }

  async getFiltersUsedBySavedSearchIds(savedSearchIds: string[]): Promise<GetFiltersUsedResponse> {
    return this.post('/V2/SavedList/GetFiltersUsedBySavedSearchIds', savedSearchIds)
  }

  /** Paged CRM drill-down: companies pushed by this export, each with its contacts. */
  async getBulkDownloadExportedData(
    bulkDownloadId: string,
    page: number,
    pageSize: number,
    search = '',
  ): Promise<ExportedDataResponse> {
    return this.post('/V2/Export/GetBulkDownloadExportedData', {
      bulkDownloadId,
      page,
      pageSize,
      search,
    })
  }

  /** Leads the preferred-title filter or contact criteria dropped from the export. */
  async getBulkDownloadExclusions(bulkDownloadId: string): Promise<ExclusionsResponse> {
    return this.post('/V2/Export/GetBulkDownloadExclusions', bulkDownloadId)
  }

  /** Per-contact errors the CRM reported during the push. */
  async getBulkDownloadErrors(bulkDownloadId: string): Promise<CrmErrorsResponse> {
    return this.post('/V2/Export/GetBulkDownloadErrors', bulkDownloadId)
  }

  async getCompaniesFromListBySimpleUrl(
    listId: string,
    leadExclusionDetails: LeadExclusionDetail[],
  ): Promise<GetCompaniesFromListResponse> {
    return this.post('/V2/SavedList/GetCompaniesFromListBySimpleUrl', {
      ListId: listId,
      LeadExclusionDetails: leadExclusionDetails,
    })
  }

  async getContactsFromListBySimpleUrl(
    listId: string,
    leadExclusionDetails: LeadExclusionDetail[],
  ): Promise<GetContactsFromListResponse> {
    return this.post('/V2/SavedList/GetContactsFromListBySimpleUrl', {
      ListId: listId,
      LeadExclusionDetails: leadExclusionDetails,
    })
  }

  async removeFromListBySimpleUrl(
    listId: string,
    simpleUrls: string[],
    bulkDownloadId: string,
  ): Promise<{ result: boolean }> {
    return this.post('/V2/SavedList/RemoveFromListBySimpleUrl', {
      ListId: listId,
      SimpleUrl: simpleUrls,
      BulkDownloadId: bulkDownloadId,
    })
  }
}
